import React, { useState } from "react";
import { Modal } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import ScrollableWrapper from "./ScrollableWrapper";
import InlineBanner from "./InlineBanner";
import BasicInformationFields from "./BasicInformationFields";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import OutlineButton from "../../../../common/ui/OutlineButton";
import { EditCustomerPersonalInfo } from "../../../../common/network/Api";
import {
  RootStateAnswers,
  answersActions,
} from "../../../questionnaire/store/answers-slice";
import {
  getBlockedLabProviders,
  getBlockedStateMessage,
  getMissingBasicInfoFields,
  isLabOrderCountry,
} from "../../utils/basicInfoValidation";
import { getStoredPhoneValidity } from "../../../../common/utils/phone";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import SecondaryButton from "../../../../common/ui/SecondaryButton";

type Props = {
  open: boolean;
  onClose: () => void;
  // Client whose profile is being completed (prescription: selected client;
  // order approval: the order's customer).
  clientId: string;
  // Called after the profile is saved successfully, to resume the gated action.
  onSaved: () => void;
  // Primary button label (default "Save"). The awaiting-details retry flow
  // relabels it to "Retry" since saving also re-submits the order.
  primaryLabel?: string;
  // Providers of the panels being ordered; some can't serve every US state.
  labProviders?: Array<string | number | null | undefined>;
};

const BasicInformationModal = ({
  open,
  onClose,
  clientId,
  onSaved,
  primaryLabel = "Save",
  labProviders = [],
}: Props): JSX.Element => {
  const theme: Theme = useTheme();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();
  const dispatch = useDispatch();
  const [openPopperId, setOpenPopperId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const personalInfoAnswers = useSelector(
    (state: RootStateAnswers) => state.answers.personalInfoAnswers
  );

  const isCountryAllowed = isLabOrderCountry(personalInfoAnswers);
  const blockedProviders = getBlockedLabProviders(
    personalInfoAnswers,
    labProviders
  );
  const isComplete =
    getMissingBasicInfoFields(personalInfoAnswers).length === 0 &&
    isCountryAllowed &&
    blockedProviders.length === 0;
  // Same verdict the phone field's hint shows — Save must not stay enabled
  // while the field is flagged invalid.
  const phoneInvalid =
    getStoredPhoneValidity(
      personalInfoAnswers.phone,
      personalInfoAnswers.countryCode
    ) === false;
  const canSave = isComplete && !phoneInvalid;

  /**
   * The profile endpoint rejects a body identical to what it already stores
   * ("no changes detected"), and that is not a failure here.
   *
   * On the awaiting-details retry the affiliate may be re-submitting an order
   * that was rejected for something outside these fields, so an unchanged
   * profile still has to reach the finalize. The request is deliberately sent
   * either way rather than skipped on a local dirty check: only the service
   * knows what it holds, and a genuine failure must still stop the flow.
   */
  const isNoChangesRejection = (error: any): boolean =>
    /no\s+changes?/i.test(getApiErrorMessage(error, ""));

  /**
   * Sync the Redux baseline so any screen tracking dirty state (e.g. the
   * Biometrics tab) treats these values as saved rather than as pending edits
   * — otherwise its Save stays active and Cancel reverts them — then resume
   * the gated action.
   */
  const treatAsSaved = () => {
    dispatch(
      answersActions.updatePersonalInfoInitialAnswers(personalInfoAnswers)
    );
    dispatch(answersActions.clearPersonalInfoModifiedFields());
    onSaved();
  };

  const bannerText = !isCountryAllowed
    ? "Lab orders can only be shipped to addresses in the United States. Please update the address to a valid US address to continue."
    : blockedProviders.length > 0
    ? getBlockedStateMessage(personalInfoAnswers, blockedProviders)
    : "To ensure secure order placement, your contact information must be complete. Please take a moment to update your details.";

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    try {
      setIsSaving(true);
      await EditCustomerPersonalInfo(clientId, {
        ...personalInfoAnswers,
        // Let the backend resolve country/state from the name/code fields that
        // Google Places provides, rather than stale numeric IDs.
        countryID: null,
        stateID: null,
      });
      treatAsSaved();
    } catch (error: any) {
      // Nothing to save is as good as saved: same bookkeeping, same resume.
      if (isNoChangesRejection(error)) {
        treatAsSaved();
        return;
      }
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(
          error,
          "Failed to save the information. Please try again."
        ),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div style={styles.modal(theme)}>
        <div style={styles.header}>
          <div style={styles.title(theme)}>Basic information</div>
          <div
            style={styles.closeButton}
            onClick={onClose}
            role="button"
            aria-label="Close"
          >
            <XIconSvg />
          </div>
        </div>

        <InlineBanner
          text={bannerText}
          backgroundColor={theme.colorTokens.W[10]}
          iconColor={theme.colorTokens.N[90]}
        />

        <div style={styles.body}>
          <ScrollableWrapper>
            <BasicInformationFields
              layout="grid"
              includeBiometricFields={false}
              openPopperId={openPopperId}
              setOpenPopperId={setOpenPopperId}
            />
          </ScrollableWrapper>
        </div>

        <div style={styles.footer}>
          <SecondaryButton label={"Cancel"} onClick={onClose} />
          <PrimaryButton
            label={isSaving ? "Saving..." : primaryLabel}
            onClick={handleSave}
            isDisable={!canSave || isSaving}
          />
        </div>
      </div>
    </Modal>
  );
};

export default BasicInformationModal;

const styles = {
  modal: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "1081px",
    maxWidth: "calc(100vw - 48px)",
    // Fixed, taller height: gives the address / country dropdowns room to open
    // inside the modal, and keeps the modal's height constant so opening a
    // dropdown can't re-center and shift the fields behind it.
    height: "80vh",
    maxHeight: "700px",
    // Let the field dropdowns spill past the modal edge instead of being clipped.
    overflow: "visible" as "visible",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    padding: "24px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    outline: "none",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  closeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    // Fill the taller modal so the fields sit at the top with empty space below
    // for dropdowns, and the footer stays pinned to the bottom.
    flex: 1,
    minHeight: 0,
    // Don't clip the field dropdowns (address suggestions, country selector).
    overflow: "visible" as "visible",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
  },
};
